import React, { ReactNode } from 'react';
import { Gql } from '@fiverr-private/dynamic_matching';
import { Stack } from '@fiverr-private/layout_components';
import { TagIcon, ClockIcon, ReturningIcon, DurationIcon, CalendarTimelineIcon } from '@fiverr-private/visuals';
import { Text } from '@fiverr-private/typography';
import { DATETIME_ELEMENT_STYLES, dateTimeFormat } from '@fiverr-private/localization';
import { AppI18n } from '../../../i18n';
import { centsToCurrency } from '../Budget/formatCurrency';
import { useBriefCreationContext } from '../../../context/BriefCreationContext';
import { BudgetAndTimelineContent, useNeedType } from './utils';

interface ChipProps {
  icon: ReactNode;
  title: ReactNode;
  text: ReactNode | string;
}

const Chip = ({ icon, title, text }: ChipProps) => (
  <Stack direction="column" gap="0.5">
    <Stack gap="1.5" wrap="nowrap">
      <Stack display="flex" alignItems="center" flexShrink="0">
        {icon}
      </Stack>
      <Stack whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
        <Text fontWeight="semibold" size="b_md">
          {title}
        </Text>
      </Stack>
    </Stack>
    <Stack whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
      <Text>{text}</Text>
    </Stack>
  </Stack>
);

export const BudgetChip = ({ finalContent }: { finalContent: BudgetAndTimelineContent }) => {
  const { pricingType, budgetInCents, hourlyBudgetRange } = finalContent;
  const { currency } = useBriefCreationContext();
  const { needType } = useNeedType();

  const isFixedPriceMode = pricingType === Gql.BriefPricingType.Fixed;

  const getCopyKey = () => {
    if (isFixedPriceMode) {
      return `sections.budget_and_timeline.chips.${!needType || needType === 'ONE_TIME' ? 'up_to' : 'up_to_delivery'}`;
    } else {
      return 'sections.budget_and_timeline.chips.range_per_hour';
    }
  };

  const getCopyParams = () => {
    if (isFixedPriceMode) {
      return {
        budget: centsToCurrency(budgetInCents as number, currency),
      };
    } else {
      return {
        min_budget: centsToCurrency(hourlyBudgetRange?.min as number, currency),
        max_budget: centsToCurrency(hourlyBudgetRange?.max as number, currency),
      };
    }
  };

  const icon = <TagIcon color="grey_1200" />;
  const title = <AppI18n k={`sections.budget_and_timeline.${pricingType?.toLowerCase() as 'hourly' | 'fixed'}`} />;
  const text = <AppI18n k={getCopyKey() as any} params={getCopyParams()} />;

  return <Chip icon={icon} title={title} text={text} />;
};

export const DeliveryTimelineChip = ({ finalContent }: { finalContent: BudgetAndTimelineContent }) => {
  const { timeline, deliveryDate } = finalContent;

  const getCopyKey = () => {
    switch (timeline) {
      case Gql.DmBriefTimeline.Flexible:
        return 'flexible';
      case Gql.DmBriefTimeline.SoonAsPossible:
        return 'asap';
      case Gql.DmBriefTimeline.GeneralTimeline:
        return 'up_to_value_unit';

      default:
        return 'flexible';
    }
  };

  const getCopyParams = () =>
    timeline === Gql.DmBriefTimeline.GeneralTimeline ? { params: finalContent.timelineDetails?.generalTimeline } : {};

  const getFormattedDate = () =>
    deliveryDate
      ? dateTimeFormat({
          value: Number(deliveryDate),
          options: {
            day: DATETIME_ELEMENT_STYLES.TWO_DIGIT,
            month: DATETIME_ELEMENT_STYLES.SHORT,
            year: DATETIME_ELEMENT_STYLES.NUMERIC,
          },
        })
      : '';

  const icon = <CalendarTimelineIcon color="grey_1200" />;
  const title = <AppI18n k={'sections.budget_and_timeline.chips.delivery_timeline'} />;
  const text =
    timeline === Gql.DmBriefTimeline.SpecificDate ? (
      getFormattedDate()
    ) : (
      <AppI18n k={`sections.budget_and_timeline.chips.${getCopyKey()}` as any} {...getCopyParams()} />
    );

  return <Chip icon={icon} title={title} text={text} />;
};

export const TimeCommitmentChip = ({ finalContent }: { finalContent: BudgetAndTimelineContent }) => {
  const { timelineDetails } = finalContent;
  const { timeCommitment } = timelineDetails ?? {};

  if (!timeCommitment) {
    return null;
  }

  const icon = <ClockIcon color="grey_1200" />;
  const title = <AppI18n k={'sections.budget_and_timeline.chips.time_commitment'} />;
  const text = <AppI18n k={`sections.budget_and_timeline.chips.${timeCommitment}` as any} />;

  return <Chip icon={icon} title={title} text={text} />;
};

export const HiringDurationChip = ({ finalContent }: { finalContent: BudgetAndTimelineContent }) => {
  const { timelineDetails } = finalContent;
  const { hiringDuration } = timelineDetails ?? {};

  if (!hiringDuration) {
    return null;
  }

  const icon = <DurationIcon color="grey_1200" />;
  const title = <AppI18n k={'sections.budget_and_timeline.chips.hiring_duration'} />;
  const text = <AppI18n k={`sections.budget_and_timeline.chips.${hiringDuration}` as any} />;

  return <Chip icon={icon} title={title} text={text} />;
};

export const DeliveriesFrequencyChip = ({ finalContent }: { finalContent: BudgetAndTimelineContent }) => {
  const { timelineDetails = {} } = finalContent;
  const { deliveriesFrequency = {} } = timelineDetails;

  const { value, unit } = deliveriesFrequency;

  if (!value || !unit) {
    return null;
  }

  const icon = <ReturningIcon color="grey_1200" />;
  const title = <AppI18n k={'sections.budget_and_timeline.chips.frequency'} />;
  const text = (
    <AppI18n
      k={`sections.budget_and_timeline.chips.value_deliveries_per_${unit}`}
      params={{ count: value.toString() }}
    />
  );

  return <Chip icon={icon} title={title} text={text} />;
};
